import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { apiClient, getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { resolveTenantSlug } from '../../lib/tenant'

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type ClientListItem = {
  id: number
  full_name: string
  medical_record_number: string
  status: string
  status_display: string
  age: number
  city: string
  state: string
  primary_guardian_name: string | null
  has_active_auth: boolean
  created_at: string
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'intake', label: 'Intake' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'discharged', label: 'Discharged' },
  { value: 'waitlist', label: 'Waitlist' },
]

export default function ClientsListPage() {
  const tenantSlug = resolveTenantSlug()
  const user = useAuthStore((s) => s.user)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  const params = useMemo(() => {
    const next: Record<string, string> = {}
    if (search.trim()) next.search = search.trim()
    if (status) next.status = status
    return next
  }, [search, status])

  const cacheKey = useMemo(() => {
    const q = search.trim().toLowerCase()
    return `tenant:${tenantSlug ?? 'unknown'}:clients:list:status=${status || 'all'}:q=${q || 'all'}`
  }, [search, status, tenantSlug])

  const clientsQuery = useOfflineFirstQuery<PaginatedResponse<ClientListItem>>({
    queryKey: ['clients', params],
    cacheKey,
    cacheMaxAgeMs: 15 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<ClientListItem>>('/api/v1/clients/', {
        params,
      })
      return res.data
    },
  })

  const canCreateClient = user?.role === 'admin' || user?.role === 'bcba'
  const rows = clientsQuery.data?.results ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-sm text-gray-600">
            Minimum necessary access enforced. All actions are logged.
          </p>
        </div>
        {canCreateClient && (
          <Link to="/app/clients/new" className="btn-primary">
            Add client
          </Link>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="client-search">
              Search
            </label>
            <input
              id="client-search"
              className="input mt-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, MRN, or email"
            />
          </div>

          <div>
            <label className="label" htmlFor="client-status">
              Status
            </label>
            <select
              id="client-status"
              className="input mt-1"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setSearch('')
                setStatus('')
              }}
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      {clientsQuery.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(clientsQuery.error)}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Client</th>
                <th className="table-header">MRN</th>
                <th className="table-header">Status</th>
                <th className="table-header">Age</th>
                <th className="table-header">Primary Guardian</th>
                <th className="table-header">Auth</th>
                <th className="table-header">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {clientsQuery.isLoading && rows.length === 0 ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="table-cell">
                      <div className="h-4 w-56 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-10 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="table-cell">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-cell text-gray-500">
                    No clients found.
                  </td>
                </tr>
              ) : (
                rows.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link
                        to={`/app/clients/${client.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {client.full_name}
                      </Link>
                    </td>
                    <td className="table-cell">{client.medical_record_number}</td>
                    <td className="table-cell">
                      <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                        {client.status_display || client.status}
                      </span>
                    </td>
                    <td className="table-cell">{client.age}</td>
                    <td className="table-cell">
                      {client.primary_guardian_name ? (
                        <span className="text-gray-900">{client.primary_guardian_name}</span>
                      ) : (
                        <span className="text-gray-500">None</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {client.has_active_auth ? (
                        <span className="inline-flex items-center rounded-md bg-green-50 border border-green-200 px-2 py-1 text-xs font-medium text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-medium text-amber-800">
                          None
                        </span>
                      )}
                    </td>
                    <td className="table-cell">
                      {client.city}, {client.state}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

